'use client';

import { FC, MutableRefObject, useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Search, X } from 'lucide-react';
import SearchSuggestionsDropdown from './SearchSuggestionsDropdown';
import SuggestedDestinations from './SuggestedDestinations';
import { useSearchAutocomplete } from '@/hotdesk-ui/hooks/useSearchAutocomplete';
import { useRecentSearches } from '@/hotdesk-ui/hooks/useRecentSearches';
import { RecentSearch } from '@/hotdesk-ui/helpers/recent-searches';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { fetchSuggestedCities, SuggestedCity } from '@/hotdesk-ui/store/slices/suggested-cities.slice';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import { resolveNearbyLocation } from '@/hotdesk-ui/helpers/nearby-location';
import { HERO_SEARCH_MARKER } from '@/hotdesk-ui/constants/layout.constant';
import styles from './HeroSearch.module.scss';
export interface ListingTypeOption {
  id: number;
  type: string;
  icon?: string;
}
export interface HeroSearchProps {
  autocompleteInput: MutableRefObject<any>;
  autocompleteContainer: MutableRefObject<any>;
  /** Fallback for "Nearby" when geolocation is unavailable or not granted —
   * prompts for the permission. */
  getPermission: () => void;
  sentData: () => void;
  /** ISO-2 code used to fetch the suggested destinations. */
  countryCode?: string;
  /** Changes when the input value is set programmatically (e.g. resolved
   * location) so the clear (X) button visibility can re-sync. */
  searchDisplayKey?: string;
  /** Called when a place is picked from the suggestions dropdown. */
  onSelect: (result: GeoCodeResult) => void;
  /** Extra class on the band, for pages that seat the bar in their own panel. */
  className?: string;
}
const HeroSearch: FC<HeroSearchProps> = ({
  autocompleteInput,
  autocompleteContainer,
  getPermission,
  sentData,
  countryCode,
  searchDisplayKey,
  onSelect,
  className
}) => {
  const t = useTranslations('landing-v4');
  const tExplore = useTranslations('explore');
  const [hasSearchValue, setHasSearchValue] = useState(false);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const dispatch = useAppDispatch();
  const suggestedCities = useAppSelector((state: RootState) => state.suggestedCities.items);
  const suggestedCitiesLoading = useAppSelector((state: RootState) => state.suggestedCities.loading);
  const currentCity = useAppSelector((state: RootState) => state.geoLocation.geoLocationZipData?.city);
  const {
    recent,
    record,
    clear: clearRecent
  } = useRecentSearches();

  // Every resolved place funnels through here, so it is the one place that
  // needs to remember the search — autocomplete picks land here via the
  // hook, suggested cities and recents call it directly.
  const handleSelect = useCallback((result: GeoCodeResult) => {
    record(result);
    onSelect(result);
  }, [record, onSelect]);
  const {
    containerRef,
    predictions,
    activeIndex,
    isOpen,
    isEmptyFocus,
    setActiveIndex,
    selectByIndex,
    selectActiveOrFirst,
    close,
    resetToEmpty
  } = useSearchAutocomplete({
    inputRef: autocompleteInput,
    onSelect: handleSelect
  });

  // Warm the suggestions as soon as we know the country, so the panel has
  // content the first time the (empty) input is focused.
  useEffect(() => {
    if (countryCode) dispatch(fetchSuggestedCities(countryCode));
  }, [countryCode, dispatch]);

  // Every place resolved outside the autocomplete — a panel row, "Nearby" —
  // lands here: show it in the box, then push it down the normal path.
  const commitResult = useCallback((result: GeoCodeResult) => {
    if (autocompleteInput.current) {
      autocompleteInput.current.value = result.formatted_address;
    }
    setHasSearchValue(true);
    close();
    handleSelect(result);
  }, [autocompleteInput, close, handleSelect]);

  // Rows in the panel aren't Google predictions, so build the same shape the
  // autocomplete would have produced.
  const selectPlace = useCallback((place: {
    label: string;
    lat: number;
    lng: number;
    city?: string;
    country?: string;
    countryCode?: string;
    placeId?: string;
    /** Country-wide row — must carry the `country` type through. */
    isCountry?: boolean;
    /** State-wide row — its English name, searched as a state. */
    regionName?: string;
  }) => {
    commitResult({
      address_components: [] as unknown as GeoCodeResult['address_components'],
      formatted_address: place.label,
      geometry: {
        location: {
          lat: place.lat,
          lng: place.lng
        },
        location_type: 'APPROXIMATE'
      },
      place_id: place.placeId || '',
      plus_code: {
        compound_code: '',
        global_code: ''
      },
      // Everything downstream (fetch by country name, `countryCode`
      // in the URL, map framing) keys off this type.
      types: (place.isCountry ? ['country'] : place.regionName ? ['administrative_area_level_1'] : []) as unknown as GeoCodeResult['types'],
      country: place.country,
      country_sort: place.countryCode,
      city: place.city,
      ...(place.regionName ? {
        state: place.regionName,
        region_name: place.regionName
      } : {}),
      lat: place.lat,
      log: place.lng,
      source: 'autocomplete'
    } as GeoCodeResult);
  }, [commitResult]);

  // "Nearby" resolves the device's position and then travels the same path as
  // a picked city — so it opens explore on that location instead of only
  // updating this page. Not granted / refused falls back to the prompt.
  const handleUseMyLocation = useCallback(() => {
    if (nearbyLoading) return;
    setNearbyLoading(true);
    resolveNearbyLocation(result => {
      setNearbyLoading(false);
      commitResult(result);
    }, () => {
      setNearbyLoading(false);
      close();
      getPermission();
    });
  }, [nearbyLoading, commitResult, close, getPermission]);
  const handleSuggestedCity = useCallback((city: SuggestedCity) => selectPlace({
    label: city.countryName ? `${city.name}, ${city.countryName}` : city.name,
    lat: city.lat,
    lng: city.lng,
    city: city.name,
    country: city.countryName,
    countryCode
  }), [selectPlace, countryCode]);
  const handleRecentSearch = useCallback((entry: RecentSearch) => selectPlace({
    label: entry.label,
    lat: entry.lat,
    lng: entry.lng,
    city: entry.city,
    country: entry.country,
    countryCode: entry.countryCode,
    placeId: entry.id.includes(',') ? undefined : entry.id,
    isCountry: entry.isCountry,
    regionName: entry.regionName
  }), [selectPlace]);

  // Search button: pick the highlighted/first suggestion when the user typed
  // but didn't choose one (onSelect then navigates to explore). With no
  // suggestions, navigate directly with the current location.
  const handleSearchClick = () => {
    if (!selectActiveOrFirst()) sentData();
  };
  const syncSearchValue = useCallback(() => {
    const value = autocompleteInput.current?.value?.trim() ?? '';
    setHasSearchValue(value.length > 0);
  }, [autocompleteInput]);

  // Reflect the resolved location in the box (so the clear "X" shows on load,
  // even when the value is set programmatically) and re-sync the clear button.
  // Direct value set — no input event — keeps the suggestions list closed.
  useEffect(() => {
    const input = autocompleteInput.current;
    if (input && searchDisplayKey && input.value !== searchDisplayKey) {
      input.value = searchDisplayKey;
    }
    syncSearchValue();
  }, [searchDisplayKey, syncSearchValue, autocompleteInput]);
  const handleClearSearch = () => {
    if (autocompleteInput.current) {
      autocompleteInput.current.value = '';
    }
    setHasSearchValue(false);
    autocompleteInput.current?.focus();
    // Drop the stale predictions and fall back to suggested destinations.
    resetToEmpty();
  };
  return <section className={className ? `${styles.hero} ${className}` : styles.hero} data-sentry-component="HeroSearch" data-sentry-source-file="HeroSearch.tsx">
            {/* Marked so the header knows to hold back its own search bar
                while this one is on screen, and to take over once it isn't. */}
            <div {...HERO_SEARCH_MARKER} className={styles.searchBarWrapper} ref={containerRef}>
                <div className={styles.searchBar} ref={autocompleteContainer}>
                    <div className={styles.searchSegment}>
                        <label className={styles.textContainer}>
                            <span className={styles.segmentLabel}>
                                {t('search.whereLabel')}
                            </span>
                            <input ref={autocompleteInput} type="text" className={styles.searchInput} placeholder={t('search.wherePlaceholder')} autoCorrect="off" autoCapitalize="off" spellCheck={false} onInput={syncSearchValue} onChange={syncSearchValue}
            // The value is often written programmatically
            // (resolved location, Google autocomplete),
            // which fires neither of the above — re-check
            // on focus so the X is right the moment the
            // field is clicked into.
            onFocus={syncSearchValue} onClick={syncSearchValue} />
                        </label>
                        {/* "Use my location" lives on the Nearby row of the
                            suggestions panel, not in the bar. */}
                        <button type="button" className={`${styles.clearBtn} ${hasSearchValue ? styles.clearBtnVisible : ''}`} onClick={handleClearSearch} aria-label={tExplore('clearSearch')} aria-hidden={!hasSearchValue} tabIndex={hasSearchValue ? 0 : -1}>
                            <X size={16} strokeWidth={2.25} data-sentry-element="X" data-sentry-source-file="HeroSearch.tsx" />
                        </button>
                    </div>

                    <button type="button" className={styles.searchButton} onClick={handleSearchClick} aria-label="Search">
                        <Search size={18} color="#fff" data-sentry-element="Search" data-sentry-source-file="HeroSearch.tsx" />
                    </button>
                </div>

                {isOpen && <SearchSuggestionsDropdown predictions={predictions} activeIndex={activeIndex} onHover={setActiveIndex} onSelect={selectByIndex} />}

                {/* Nothing typed yet — offer cities in the visitor's country. */}
                {!isOpen && isEmptyFocus && <SuggestedDestinations cities={suggestedCities} loading={suggestedCitiesLoading} currentCity={currentCity} onSelect={handleSuggestedCity} onUseMyLocation={handleUseMyLocation} nearbyLoading={nearbyLoading} recent={recent} onSelectRecent={handleRecentSearch} onClearRecent={clearRecent} />}
            </div>
        </section>;
};
export default HeroSearch;